import { PlusIcon } from '@commenda/icons';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@commenda/kakashi/src/internal/Command';
import { cn } from '@commenda/utils';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

interface Option {
  value: string;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
}

export interface AsyncComboboxProps {
  _className?: string;
  options: Option[];
  renderOption?: (props: {
    option: Option;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: (value: string) => void;
  }) => React.ReactNode;
  renderTrigger?: (props: {
    selectedOption: Option | undefined;
    placeholder: string;
  }) => React.ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  addItemConfig?: {
    shouldAllowAdd: boolean;
    onAddItem: () => void;
    placeholder: string;
  };
  notFoundText?: string;
  value: string;
  setValue: (val: string) => void;
  error?: string;
  disabled?: boolean;
  id?: string;
  hideSearch?: boolean;
  canClearSelection?: boolean;
  _optionsClassName?: string;
  useRenderTriggerForPlaceholder?: boolean;

  onSearch?: (query: string) => void;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const AsyncCombobox: React.FC<AsyncComboboxProps> = ({
  _className,
  options,
  renderOption,
  addItemConfig,
  renderTrigger,
  placeholder = 'Select option',
  searchPlaceholder = 'Search option...',
  notFoundText = 'No results.',
  setValue,
  value,
  error = false,
  disabled = false,
  id,
  hideSearch = false,
  _optionsClassName,
  canClearSelection = true,
  useRenderTriggerForPlaceholder = false,

  onSearch,
  isLoading = false,
  onLoadMore,
  hasMore = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleSelect = (currentValue: string) => {
    const option = options.find((option) => option.value === currentValue);
    if (option?.disabled) return;

    setValue(canClearSelection && currentValue === value ? '' : currentValue);
    setOpen(false);
  };

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = options.filter((option) =>
    option?.label?.toLowerCase()?.includes(searchQuery.toLowerCase())
  );

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch?.(query);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      handleSearch('');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          disabled={disabled}
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex h-11 w-full border border-border-primary items-center justify-between rounded-md focus-visible:border-2 bg-bg-primary px-[14px] py-2.5 txt-medium-regular ring-0 placeholder:text-text-placeholder focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle [&>span]:line-clamp-1',
            error
              ? 'border-border-error focus-visible:border-border-error'
              : '',
            !value ? 'text-text-placeholder' : '',
            _className
          )}
        >
          {renderTrigger &&
          (useRenderTriggerForPlaceholder || selectedOption) ? (
            renderTrigger({
              selectedOption,
              placeholder,
            })
          ) : (
            <span
              className={cn(
                'flex-grow text-left',
                !selectedOption && 'text-text-placeholder txt-medium-regular'
              )}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', _optionsClassName)}
        align="start"
        style={{
          width: 'var(--radix-popper-anchor-width)',
        }}
      >
        <Command shouldFilter={false}>
          {!hideSearch && (
            <div className="sticky top-0 bg-white z-10">
              <CommandInput
                data-testid="combobox-search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onValueChange={handleSearch}
              />
            </div>
          )}
          {addItemConfig?.shouldAllowAdd && (
            <div className="sticky top-[var(--command-input-height)] bg-white z-10 border-b border-border-primary">
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  addItemConfig.onAddItem();
                }}
                className="mx-0 rounded-none justify-start"
              >
                <PlusIcon className="mr-2 size-4 text-fg-quinary" />
                {addItemConfig.placeholder}
              </CommandItem>
            </div>
          )}

          {filteredOptions.length === 0 && (
            <CommandList className="max-h-60">
              <CommandEmpty>{notFoundText}</CommandEmpty>
            </CommandList>
          )}

          {filteredOptions.length > 0 && (
            <CommandList
              className="max-h-80 overflow-y-auto"
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              <CommandGroup>
                {filteredOptions
                  .filter((option) => !option.hidden)
                  .map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      onSelect={handleSelect}
                    >
                      {renderOption ? (
                        renderOption({
                          option,
                          isSelected: value === option.value,
                          isDisabled: !!option.disabled,
                          onSelect: handleSelect,
                        })
                      ) : (
                        <>
                          {option.label}
                          <Check
                            className={cn(
                              'ml-auto h-4 w-4',
                              value === option.value
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                        </>
                      )}
                    </CommandItem>
                  ))}
              </CommandGroup>

              {isLoading && (
                <div className="sticky bottom-0 bg-white z-10 flex items-center justify-center p-2 border-t border-border-primary">
                  <span className="txt-small-regular text-text-secondary">
                    Loading...
                  </span>
                </div>
              )}

              {hasMore && !isLoading && filteredOptions.length > 0 && (
                <div className="bg-white z-10 border-t border-border-primary">
                  <CommandItem
                    onSelect={() => {
                      onLoadMore?.();
                    }}
                    className="justify-center text-text-primary hover:bg-bg-secondary"
                  >
                    Load more
                  </CommandItem>
                </div>
              )}
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { AsyncCombobox };
